import { useState, type CSSProperties } from 'react';
import type { BackgroundTask } from '../runtime/background-tasks';
import { useBackgroundTaskStore, useBackgroundTasks } from './background-tasks';
import './background-task-chip.scss';

/** The tasks the chip shows: a result the manager already closed keeps waiting on its dish, silently. */
const visibleTasks = (tasks: readonly BackgroundTask[]) => tasks.filter((task) => !(task.state === 'ready' && task.acknowledged));

/** One line per state, in plain words: no minutes, costs or jobs. */
function backgroundTaskLine(task: BackgroundTask): string {
  const name = `«${task.subjectName}»`;
  switch (task.state) {
    case 'running':
      return `Mejorando la foto de ${name}…`;
    case 'ready':
      return `Foto lista: ${name}`;
    case 'failed':
      return `No se pudo mejorar la foto de ${name}`;
    case 'interrupted':
      return `La foto de ${name} no terminó al recargar la página`;
  }
}

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** Owner decision 4: more than one task is one summary line. */
function backgroundTaskSummary(tasks: readonly BackgroundTask[]): string {
  const running = tasks.filter((task) => task.state === 'running').length;
  const ready = tasks.filter((task) => task.state === 'ready').length;
  const unfinished = tasks.length - running - ready;
  return [
    running > 0 ? `Mejorando ${count(running, 'foto', 'fotos')}…` : null,
    ready > 0 ? count(ready, 'foto lista', 'fotos listas') : null,
    unfinished > 0 ? count(unfinished, 'sin terminar', 'sin terminar') : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** Assumed length of one enhancement for the progress line: it creeps towards the end and never claims it. */
const EXPECTED_MS = 60_000;

function TaskGlyph({ state }: { readonly state: BackgroundTask['state'] }) {
  if (state === 'running') return <span aria-hidden="true" className="bg-task__spinner" />;
  return (
    <span aria-hidden="true" className={`bg-task__glyph bg-task__glyph--${state === 'ready' ? 'ready' : 'issue'}`}>
      <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" viewBox="0 0 24 24" width="14">
        {state === 'ready' ? <path d="m5 12.5 4.5 4.5L19 7.5" /> : <path d="M12 7v6M12 17h.01" />}
      </svg>
    </span>
  );
}

function TaskRow({
  task,
  now,
  disabled,
  onOpen,
  onRetry,
  onDismiss,
}: {
  readonly task: BackgroundTask;
  readonly now: number;
  readonly disabled: boolean;
  readonly onOpen: (task: BackgroundTask) => void;
  readonly onRetry: (task: BackgroundTask) => void;
  readonly onDismiss: (task: BackgroundTask) => void;
}) {
  const line = backgroundTaskLine(task);
  return (
    <div className={`bg-task bg-task--${task.state}`} data-background-task={task.id} data-state={task.state}>
      <TaskGlyph state={task.state} />
      <span className="bg-task__text" title={line}>
        {line}
      </span>
      {task.state === 'ready' && (
        <button className="bg-task__action" disabled={disabled} onClick={() => onOpen(task)} type="button">
          Ver
        </button>
      )}
      {task.state === 'failed' && task.retryable && (
        <button className="bg-task__action" disabled={disabled} onClick={() => onRetry(task)} type="button">
          Reintentar
        </button>
      )}
      {(task.state === 'interrupted' || (task.state === 'failed' && !task.retryable)) && (
        <button className="bg-task__action" disabled={disabled} onClick={() => onOpen(task)} type="button">
          Ver plato
        </button>
      )}
      {task.state !== 'running' && (
        <button aria-label={`Cerrar aviso: ${line}`} className="bg-task__dismiss" onClick={() => onDismiss(task)} title="Cerrar aviso" type="button">
          <svg aria-hidden="true" fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeWidth="2" viewBox="0 0 24 24" width="14">
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>
      )}
      {task.state === 'running' && (
        <span
          aria-hidden="true"
          className="bg-task__progress"
          style={{ '--bg-task-elapsed': `-${Math.min(Math.max(0, now - task.startedAt), EXPECTED_MS)}ms` } as CSSProperties}
        />
      )}
    </div>
  );
}

interface BackgroundTaskChipProps {
  readonly tasks: readonly BackgroundTask[];
  /** A frozen session pauses the actions; closing a notice stays possible. */
  readonly disabled?: boolean;
  readonly onOpen: (task: BackgroundTask) => void;
  readonly onRetry: (task: BackgroundTask) => void;
  readonly onDismiss: (task: BackgroundTask) => void;
  /** Specs pin the clock of the progress line. */
  readonly now?: number;
}

/**
 * The one-line notice above every screen (owner decision 2, 8 Oct 2026): what is
 * being made, what is ready to look at and what did not work, with one action each.
 * More than one task folds into a summary line that opens the list.
 */
function BackgroundTaskChip({ tasks: all, disabled = false, onOpen, onRetry, onDismiss, now = Date.now() }: BackgroundTaskChipProps) {
  const [expanded, setExpanded] = useState(false);
  const tasks = visibleTasks(all);
  if (tasks.length === 0) return null;
  const row = (task: BackgroundTask) => (
    <TaskRow disabled={disabled} key={task.id} now={now} onDismiss={onDismiss} onOpen={onOpen} onRetry={onRetry} task={task} />
  );
  if (tasks.length === 1) {
    return (
      <div aria-label="Tareas en segundo plano" className="bg-task-bar" role="region">
        <div aria-live="polite" className="bg-task-chip" role="status">
          {row(tasks[0]!)}
        </div>
      </div>
    );
  }
  const summary = backgroundTaskSummary(tasks);
  const running = tasks.some((task) => task.state === 'running');
  const ready = !running && tasks.some((task) => task.state === 'ready');
  return (
    <div aria-label="Tareas en segundo plano" className="bg-task-bar" role="region">
      <div className="bg-task-chip bg-task-chip--summary" data-expanded={expanded}>
        <button aria-expanded={expanded} className="bg-task-chip__summary" onClick={() => setExpanded((open) => !open)} type="button">
          <TaskGlyph state={running ? 'running' : ready ? 'ready' : 'failed'} />
          <span aria-live="polite" className="bg-task__text" role="status">
            {summary}
          </span>
          <span className="bg-task-chip__toggle">{expanded ? 'Ocultar' : 'Ver todo'}</span>
        </button>
        {expanded && <div className="bg-task-chip__list">{tasks.map(row)}</div>}
      </div>
    </div>
  );
}

/** The chip bound to the session's holder; «Ver» hands the task to its screen and goes there. */
export function BackgroundTaskStatus({
  disabled = false,
  onNavigate,
}: {
  readonly disabled?: boolean;
  readonly onNavigate: (path: string) => void;
}) {
  const store = useBackgroundTaskStore();
  const tasks = useBackgroundTasks();
  if (!store) return null;
  return (
    <BackgroundTaskChip
      disabled={disabled}
      onDismiss={(task) => store.dismiss(task.id)}
      onOpen={(task) => {
        store.requestOpen(task.id);
        onNavigate(BACKGROUND_TASK_PATH[task.kind]);
      }}
      onRetry={(task) => store.retry(task.id)}
      tasks={tasks}
    />
  );
}

const BACKGROUND_TASK_PATH: Readonly<Record<BackgroundTask['kind'], string>> = Object.freeze({
  photo_enhancement: '/app/menu',
});
